import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { businessConsultantService } from '../../../services/businessConsultantService';
import type { BusinessConsultantChat, BusinessConsultantMessage, ToolResultAction } from '../../../types/businessConsultant';
import type { Business } from '../../../types/business';
import { useLocation, useNavigate } from 'react-router-dom';
import { evaluateLocalActions } from '../../../modules/intentMatcher';
import { requestTTS } from '../../../services/voiceService';
import { CRM_ADVISOR_INSIGHTS_ENABLED } from '../../../config/featureFlags';
import ToolsHelpModal from '../../../components/business-consultant/ToolsHelpModal';
import ConversationStarters from '../../../components/business-consultant/ConversationStarters';
import MessageActions from '../../../components/business-consultant/MessageActions';
import ProactiveInsights from '../../../components/business-consultant/ProactiveInsights';
import ToolResultCard from '../../../components/business-consultant/ToolResultCard';
import AdvisorInsightsPanel from '../../../components/business-consultant/AdvisorInsightsPanel';
import { useCrmOrgStore } from '../../../store/crmOrg.store';
import { generateAdvisorInsights, AdvisorInsightsResult } from '../../../services/businessConsultantAdvisorService';
import { anthropicService } from '../../../services/anthropicService';

interface ChatTabProps {
  chats?: BusinessConsultantChat[];
  setChats?: React.Dispatch<React.SetStateAction<BusinessConsultantChat[]>>;
  businesses?: Business[];
  selectedBusinessId?: string | null;
  onNewChat?: () => void;
  selectedChat?: BusinessConsultantChat | null;
}

const consultantPersonas = {
  marketing: {
    name: 'Keith',
    title: 'Marketing Strategist'
  },
  finance: {
    name: 'Carol',
    title: 'Financial Advisor'
  },
  strategy: {
    name: 'Nikita',
    title: 'Business Strategist'
  }
};

const ChatTab: React.FC<ChatTabProps> = ({ 
  chats = [], 
  setChats,
  businesses = [],
  selectedBusinessId,
  onNewChat,
  selectedChat
}) => {
  const { user, isPremium } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeOrgId } = useCrmOrgStore();
  const [currentChat, setCurrentChat] = useState<BusinessConsultantChat | null>(null);
  const [messages, setMessages] = useState<BusinessConsultantMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [useBusinessContext, setUseBusinessContext] = useState(true);
  const [showToolsHelp, setShowToolsHelp] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [advisorInsightsByChat, setAdvisorInsightsByChat] = useState<Record<string, AdvisorInsightsResult>>({});
  const [advisorInsightsLoading, setAdvisorInsightsLoading] = useState(false);
  const [advisorInsightsError, setAdvisorInsightsError] = useState<string | null>(null);

  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState<boolean>(false);
  const [speechError, setSpeechError] = useState<string | null>(null);
  const [partialTranscript, setPartialTranscript] = useState<string>('');
  const [autoSendVoice, setAutoSendVoice] = useState<boolean>(false);
  const recognitionRef = useRef<any>(null);
  const pttActiveRef = useRef<boolean>(false);
  const [useBackendSTT] = useState<boolean>(true);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaChunksRef = useRef<Blob[]>([]);

  const [enableTTS, setEnableTTS] = useState<boolean>(false);
  const ttsAudioRef = useRef<HTMLAudioElement | null>(null);
  const lastTTSMessageIdRef = useRef<string | null>(null);
  const memorySummarizingRef = useRef(false);

  const promptConsumedRef = useRef(false);

  // Helper to detect if message content is a tool result
  const isToolResult = (content: string): boolean => {
    try {
      const parsed = JSON.parse(content);
      return parsed && typeof parsed === 'object' && parsed.action && parsed.success !== undefined;
    } catch {
      return false;
    }
  };

  // Helper to parse tool result
  const parseToolResult = (content: string) => {
    try {
      return JSON.parse(content);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem('bc_use_business_context');
      if (raw === 'true' || raw === 'false') {
        setUseBusinessContext(raw === 'true');
      }
      const tts = localStorage.getItem('bc_enable_tts');
      const voice = localStorage.getItem('bc_auto_send_voice');
      if (tts === 'true' || tts === 'false') setEnableTTS(tts === 'true');
      if (voice === 'true' || voice === 'false') setAutoSendVoice(voice === 'true');
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'bc_use_business_context') {
        if (e.newValue === 'true' || e.newValue === 'false') {
          setUseBusinessContext(e.newValue === 'true');
        }
      }
      if (e.key === 'bc_enable_tts') {
        if (e.newValue === 'true' || e.newValue === 'false') {
          setEnableTTS(e.newValue === 'true');
        }
      }
      if (e.key === 'bc_auto_send_voice') {
        if (e.newValue === 'true' || e.newValue === 'false') {
          setAutoSendVoice(e.newValue === 'true');
        }
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (selectedChat) {
      setCurrentChat(selectedChat);
    }
  }, [selectedChat]);

  useEffect(() => {
    const run = async () => {
      if (!user?.id) return;
      if (promptConsumedRef.current) return;

      let prompt: string | null = null;
      try {
        const params = new URLSearchParams(location.search || '');
        prompt = params.get('prompt');
      } catch {
        prompt = null;
      }

      const trimmed = String(prompt || '').trim();
      if (!trimmed) return;

      promptConsumedRef.current = true;

      try {
        let chat = currentChat;
        if (!chat) {
          const newChat = await businessConsultantService.createChat({
            chat_name: 'Welcome Chat',
            business_id: useBusinessContext && selectedBusinessId ? selectedBusinessId : undefined,
            consultant_type: 'strategy'
          });

          if (setChats) {
            setChats((prev) => [newChat, ...prev]);
          }
          chat = newChat;
          setCurrentChat(newChat);
        }

        await businessConsultantService.sendMessageToChatAndGetResponse(
          chat.id,
          trimmed,
          useBusinessContext && !!(selectedBusinessId || (chat as any)?.business_id),
          useBusinessContext
            ? (selectedBusinessId || (chat as any)?.business_id || undefined)
            : undefined
        );
        await loadMessages();

        try {
          const params = new URLSearchParams(location.search || '');
          params.delete('prompt');
          const nextSearch = params.toString();
          navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
        } catch {
        }
      } catch (err: any) {
        console.error('Failed to process seeded prompt:', err);
        toast.error(err?.message || 'Failed to start your onboarding chat');
      }
    };

    void run();
  }, [location.search, user?.id, currentChat, selectedBusinessId, useBusinessContext, setChats]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (currentChat) {
      loadMessages();
    }
  }, [currentChat?.id]);

  const shouldShowAdvisorPanel = CRM_ADVISOR_INSIGHTS_ENABLED && !!currentChat;
  const currentAdvisorInsights = currentChat ? advisorInsightsByChat[currentChat.id] ?? null : null;
  const advisorPanelDisabledReason = !activeOrgId ? 'Select an active CRM workspace to enable advisor insights.' : null;

  useEffect(() => {
    setAdvisorInsightsError(null);
  }, [currentChat?.id]);

  const effectiveBusinessId = selectedBusinessId || (currentChat as any)?.business_id || null;

  const loadMessages = async (): Promise<BusinessConsultantMessage[]> => {
    if (!currentChat) return [];
    try {
      const chatMessages = await businessConsultantService.getMessagesByChatId(currentChat.id);
      setMessages(chatMessages);
      return chatMessages;
    } catch (err: any) {
      console.error('Failed to load messages:', err);
      toast.error('Failed to load messages');
    }
    return [];
  };

  const handleCreateNewChat = async () => {
    if (!user?.id) return;
    
    try {
      const newChat = await businessConsultantService.createChat({
        chat_name: 'New Conversation',
        business_id: useBusinessContext && selectedBusinessId ? selectedBusinessId : undefined,
        consultant_type: 'strategy'
      });
      
      if (setChats) {
        setChats(prev => [newChat, ...prev]);
      }
      setCurrentChat(newChat);
      setMessages([]);
      toast.success('New conversation started');
    } catch (err: any) {
      console.error('Failed to create chat:', err);
      toast.error('Failed to start new conversation');
    }
  };

  const sendMessage = async (text: string, options?: { clearInput?: boolean }) => {
    if (!text.trim() || !currentChat || isTyping) return;

    const userMessage = text.trim();
    if (options?.clearInput) setMessageInput('');
    setIsTyping(true);

    if (useBusinessContext && !effectiveBusinessId) {
      toast('Business context is enabled, but no business is selected. Sending without business context.');
    }

    try {
      const local = evaluateLocalActions(userMessage);
      if (local?.actionType === 'cta' && local.route) {
        await businessConsultantService.createMessage({
          chat_id: currentChat.id,
          role: 'user',
          content: userMessage,
        });

        await businessConsultantService.createMessage({
          chat_id: currentChat.id,
          role: 'assistant',
          content: local.text || 'I can open a tool to help with that.',
        });

        const envelope = {
          type: 'tool-cta',
          toolId: local.toolId,
          text: local.text,
          route: local.route,
          ctaLabel: local.ctaLabel,
        };

        await businessConsultantService.createMessage({
          chat_id: currentChat.id,
          role: 'assistant',
          content: JSON.stringify(envelope),
        });

        await loadMessages();
        toast.success(local.text);
        return;
      }

      await businessConsultantService.sendMessageToChatAndGetResponse(
        currentChat.id,
        userMessage,
        useBusinessContext && !!effectiveBusinessId,
        useBusinessContext && effectiveBusinessId ? effectiveBusinessId : undefined
      );

      const updatedMessages = await loadMessages();
      await maybeSummarizeChatMemory(updatedMessages);
    } catch (err: any) {
      console.error('Failed to send message:', err);
      toast.error(err?.message || 'Failed to send message');
    } finally {
      setIsTyping(false);
    }
  };

  const handleSendMessage = async () => {
    await sendMessage(messageInput, { clearInput: true });
  };

  const handleToolAction = async (action: ToolResultAction) => {
    if (!action) return;
    if (action.type === 'navigate' && action.route) {
      navigate(action.route);
      return;
    }
    if (action.type === 'prefill' && action.value) {
      setMessageInput(action.value);
      return;
    }
    if (action.type === 'send_message' && action.message) {
      await sendMessage(action.message, { clearInput: true });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleGenerateAdvisorInsights = async () => {
    if (!CRM_ADVISOR_INSIGHTS_ENABLED) {
      const message = 'CRM advisor insights are disabled in this environment.';
      setAdvisorInsightsError(message);
      toast.error(message);
      return;
    }

    if (!currentChat) {
      const message = 'Open a consultant chat to generate advisor insights.';
      setAdvisorInsightsError(message);
      toast.error(message);
      return;
    }

    if (!activeOrgId) {
      const message = 'Select an active CRM workspace from the CRM section to enable advisor insights.';
      setAdvisorInsightsError(message);
      toast.error(message);
      return;
    }

    if (!isPremium) {
      const message = 'Advisor insights require an active Premium subscription.';
      setAdvisorInsightsError(message);
      toast.error(message);
      return;
    }

    const consultantType = (currentChat as any)?.consultant_type || 'strategy';
    const persona = consultantPersonas[consultantType as keyof typeof consultantPersonas] || consultantPersonas.strategy;
    const businessName = (currentChat as any)?.business_id
      ? businesses.find((b) => b.id === (currentChat as any).business_id)?.name
      : undefined;

    setAdvisorInsightsLoading(true);
    setAdvisorInsightsError(null);

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      const baseResult = await generateAdvisorInsights({
        organizationId: activeOrgId,
        consultantName: persona.name,
        consultantTitle: persona.title,
        businessName,
        timeframeLabel: 'Last 30 days',
        timezone,
        rangeDays: 30,
        onlyMine: false,
      });

      let narrative = '';
      try {
        const compiledPrompt = [
          baseResult.prompt.system,
          '',
          baseResult.prompt.user,
          '',
          'Deliver a concise CRM advisor brief with headings for Risks, Opportunities, Data Hygiene, and Next Actions. Use short bullet points and keep it under 200 words.',
        ].join('\n');
        narrative = await anthropicService.generateText(compiledPrompt);
      } catch (llmErr) {
        console.warn('Advisor insights narrative generation failed:', llmErr);
      }

      const finalResult: AdvisorInsightsResult = {
        ...baseResult,
        markdown: narrative && narrative.trim().length > 0 ? narrative.trim() : baseResult.markdown,
      };

      setAdvisorInsightsByChat((prev) => ({
        ...prev,
        [currentChat.id]: finalResult,
      }));

      try {
        await businessConsultantService.createMessage({
          chat_id: currentChat.id,
          role: 'assistant',
          content: JSON.stringify({
            type: 'advisor-insights',
            summary: finalResult.summary,
            markdown: finalResult.markdown,
            generatedAt: finalResult.generatedAt,
            actions: finalResult.actions,
            source: finalResult.source,
            confidence: finalResult.confidence,
          })
        });
        await loadMessages();
      } catch (err) {
        console.warn('Failed to persist advisor insights message:', err);
      }

      toast.success('Advisor insights updated.');
    } catch (error: any) {
      const message = error?.message || 'Unable to generate advisor insights. Please try again later.';
      console.error('Advisor insights generation error:', error);
      setAdvisorInsightsError(message);
      toast.error(message);
    } finally {
      setAdvisorInsightsLoading(false);
    }
  };

  const handleEditMessage = async (messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (!message) return;
    
    setEditingMessageId(messageId);
    setEditingContent(message.content);
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId || !currentChat) return;
    
    try {
      setIsTyping(true);
      await businessConsultantService.editMessage(editingMessageId, editingContent);
      setEditingMessageId(null);
      setEditingContent('');
      
      // Regenerate response with edited message
      await businessConsultantService.sendMessageToChatAndGetResponse(
        currentChat.id,
        editingContent,
        useBusinessContext && !!effectiveBusinessId,
        useBusinessContext && effectiveBusinessId ? effectiveBusinessId : undefined,
        { skipUserMessageCreate: true }
      );
      
      await loadMessages();
      toast.success('Message edited and response regenerated');
    } catch (err: any) {
      console.error('Failed to edit message:', err);
      toast.error(err?.message || 'Failed to edit message');
    } finally {
      setIsTyping(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingContent('');
  };

  const handleRegenerateResponse = async () => {
    if (!currentChat) return;
    
    try {
      setIsTyping(true);
      await businessConsultantService.regenerateResponse(currentChat.id);
      await loadMessages();
      toast.success('Response regenerated');
    } catch (err: any) {
      console.error('Failed to regenerate response:', err);
      toast.error(err?.message || 'Failed to regenerate response');
    } finally {
      setIsTyping(false);
    }
  };

  const handleFeedback = async (messageId: string, sentiment: 'positive' | 'negative') => {
    try {
      await businessConsultantService.recordFeedback(messageId, sentiment);
      toast.success(sentiment === 'positive' ? 'Thanks for the feedback! 👍' : 'Feedback recorded. We\'ll improve! 👎');
    } catch (err) {
      console.error('Failed to record feedback:', err);
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    setMessageInput(prompt);
    setTimeout(() => handleSendMessage(), 100);
  };

  const shouldSkipEnvelope = (content: string) => {
    try {
      const parsed = JSON.parse(content);
      if (!parsed || typeof parsed !== 'object') return false;
      if (parsed.type) return true;
      if (parsed.action && parsed.success !== undefined) return true;
    } catch {
      return false;
    }
    return false;
  };

  const maybeSummarizeChatMemory = async (chatMessages: BusinessConsultantMessage[]) => {
    if (!currentChat || memorySummarizingRef.current) return;
    if (!Array.isArray(chatMessages) || chatMessages.length < 6) return;

    const recentWindow = chatMessages.slice(-8);
    const hasRecentMemory = recentWindow.some((msg) => {
      if (msg.role !== 'assistant') return false;
      try {
        const parsed = JSON.parse(msg.content);
        return parsed?.type === 'chat-memory';
      } catch {
        return false;
      }
    });
    if (hasRecentMemory) return;

    const transcript = chatMessages
      .filter((msg) => !shouldSkipEnvelope(msg.content))
      .slice(-12)
      .map((msg) => `${msg.role === 'user' ? 'User' : 'Advisor'}: ${msg.content}`)
      .join('\n');

    if (!transcript.trim()) return;
    memorySummarizingRef.current = true;
    try {
      const prompt = [
        'Summarize this conversation into 4-6 bullet points capturing goals, constraints, decisions, and next steps.',
        'Keep it concise and actionable. Avoid repeating tool outputs.',
        '',
        transcript
      ].join('\n');
      const summary = await anthropicService.generateText(prompt);
      if (!summary?.trim()) return;
      await businessConsultantService.createMessage({
        chat_id: currentChat.id,
        role: 'assistant',
        content: JSON.stringify({
          type: 'chat-memory',
          summary: summary.trim(),
          generatedAt: new Date().toISOString(),
        })
      });
      await loadMessages();
    } catch (err) {
      console.warn('Chat memory summarization failed:', err);
    } finally {
      memorySummarizingRef.current = false;
    }
  };

  const renderChatMemoryCard = (payload: any) => {
    if (!payload || typeof payload !== 'object') return null;
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Session Memory</h4>
            <p className="text-xs text-gray-500">Quick recap to keep context tight</p>
          </div>
          {payload.generatedAt && (
            <span className="text-[11px] text-gray-400">{new Date(payload.generatedAt).toLocaleString()}</span>
          )}
        </div>
        {payload.summary && (
          <div className="prose prose-sm max-w-none text-gray-700 mt-3">
            <ReactMarkdown>{payload.summary}</ReactMarkdown>
          </div>
        )}
      </div>
    );
  };

  const renderAdvisorInsightsCard = (payload: any) => {
    if (!payload || typeof payload !== 'object') return null;
    const actions = Array.isArray(payload.actions) ? payload.actions : [];
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Advisor Insights</h4>
            <p className="text-xs text-gray-500">Snapshot for your CRM pipeline</p>
          </div>
          {payload.generatedAt && (
            <span className="text-[11px] text-gray-400">{new Date(payload.generatedAt).toLocaleString()}</span>
          )}
        </div>
        {payload.markdown && (
          <div className="prose prose-sm max-w-none text-gray-700 mt-3">
            <ReactMarkdown>{payload.markdown}</ReactMarkdown>
          </div>
        )}
        {(payload.source || typeof payload.confidence === 'number') && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
            {payload.source && <span className="rounded-full bg-gray-100 px-2 py-0.5">Source: {payload.source}</span>}
            {typeof payload.confidence === 'number' && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">
                Confidence: {(payload.confidence * 100).toFixed(0)}%
              </span>
            )}
          </div>
        )}
        {actions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {actions.map((action: ToolResultAction, idx: number) => (
              <button
                key={`${action.label}-${idx}`}
                type="button"
                onClick={() => handleToolAction(action)}
                className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow-sm transition hover:border-blue-200 hover:text-blue-700"
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSpeechSupported(false);
      return;
    }
    setSpeechSupported(true);
    const rec = new SpeechRecognition();
    rec.lang = 'en-US';
    rec.interimResults = true;
    rec.continuous = false;

    rec.onresult = (event: any) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) final += res[0].transcript;
        else interim += res[0].transcript;
      }
      setPartialTranscript(interim);
      if (final) {
        const fin = final.trim();
        if (fin) {
          if (autoSendVoice) {
            setPartialTranscript('');
            setTimeout(() => { if (fin) sendMessage(fin, { clearInput: true }); }, 50);
          } else {
            setMessageInput(prev => (prev ? `${prev} ${fin}`.trim() : fin));
          }
        }
      }
    };
    rec.onerror = (e: any) => {
      setSpeechError(e?.error || 'Speech recognition error');
      setIsListening(false);
      pttActiveRef.current = false;
    };
    rec.onend = () => {
      setIsListening(false);
      pttActiveRef.current = false;
      setPartialTranscript('');
    };
    recognitionRef.current = rec;
    return () => {
      try { rec.abort(); } catch {}
      recognitionRef.current = null;
    };
  }, [autoSendVoice]);

  const startListening = () => {
    if (!speechSupported || !recognitionRef.current || isListening) return;
    setSpeechError(null);
    setPartialTranscript('');
    try {
      recognitionRef.current.start();
      setIsListening(true);
      pttActiveRef.current = true;
    } catch (e) {
      setSpeechError('Unable to start microphone');
    }
  };

  const stopListening = () => {
    if (!recognitionRef.current) return;
    try { recognitionRef.current.stop(); } catch {}
    setIsListening(false);
    pttActiveRef.current = false;
  };

  const startBackendRecording = async () => {
    if (isListening) return;
    setSpeechError(null);
    setPartialTranscript('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mimeCandidates = [
        'audio/ogg;codecs=opus',
        'audio/webm;codecs=opus',
        'audio/ogg',
        'audio/webm'
      ];
      const selectedMime = mimeCandidates.find(m => (window as any).MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
      const rec = new MediaRecorder(stream, selectedMime ? { mimeType: selectedMime } : undefined);
      mediaChunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) mediaChunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        try {
          const blob = new Blob(mediaChunksRef.current, { type: rec.mimeType || selectedMime || 'audio/webm' });
          if (!blob || blob.size === 0) {
            setSpeechError('Recorded audio was empty');
            return;
          }
          const arrayBuf = await blob.arrayBuffer();
          const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
            let binary = '';
            const bytes = new Uint8Array(buffer);
            const len = bytes.byteLength;
            for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i]);
            return window.btoa(binary);
          };
          const base64 = arrayBufferToBase64(arrayBuf);
          const contentType = blob.type || selectedMime || 'audio/webm';
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData?.session?.access_token;
          const userId = sessionData?.session?.user?.id;
          const resp = await fetch('/api/voice/stt', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              userId,
              audioBase64: base64,
              contentType,
              filename: `recording.${contentType.includes('ogg') ? 'ogg' : 'webm'}`
            }),
          });
          if (!resp.ok) {
            let message = 'Transcription failed';
            try {
              const j = await resp.json();
              if (j?.error) message = j.error;
              if (resp.status === 413) {
                message = 'Audio too large. Please record a shorter message.';
              } else if (resp.status === 429) {
                message = 'You are sending requests too quickly. Please wait a few seconds and try again.';
              } else if (resp.status === 422 && j?.details?.status) {
                const det = j.details;
                message = `STT error (${det.status}). ${det.body ? String(det.body).slice(0, 120) : 'Please try again.'}`;
              }
              if (j?.reqId) {
                console.warn('STT failure reqId:', j.reqId);
              }
            } catch {}
            setSpeechError(message);
            toast.error(message);
            return;
          }
          const j: any = await resp.json();
          const text: string = (j?.text || '').trim();
          if (text) {
            if (autoSendVoice) {
              setPartialTranscript('');
              setTimeout(() => { if (text) sendMessage(text, { clearInput: true }); }, 50);
            } else {
              setMessageInput(prev => (prev ? `${prev} ${text}`.trim() : text));
            }
          } else {
            setSpeechError('No speech detected or transcription empty');
            toast.error('No speech detected or STT returned empty. Please try again.');
          }
        } catch (err) {
          setSpeechError('Recording or upload failed');
        } finally {
          mediaRecorderRef.current = null;
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(t => t.stop());
            mediaStreamRef.current = null;
          }
          setIsListening(false);
          pttActiveRef.current = false;
          mediaChunksRef.current = [];
        }
      };
      mediaRecorderRef.current = rec;
      const MAX_RECORDING_MS = 20_000;
      rec.start();
      setTimeout(() => {
        try { if (rec.state === 'recording') rec.stop(); } catch {}
      }, MAX_RECORDING_MS);
      setIsListening(true);
      pttActiveRef.current = true;
    } catch (e) {
      setSpeechError('Microphone permission denied or unsupported');
    }
  };

  const stopBackendRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
  };

  const handleMicMouseDown = () => {
    if (useBackendSTT) startBackendRecording(); else startListening();
  };
  const handleMicMouseUp = () => {
    const wasActive = pttActiveRef.current;
    if (useBackendSTT) {
      stopBackendRecording();
    } else {
      stopListening();
      if (wasActive) {
        setTimeout(() => { if (messageInput.trim()) sendMessage(messageInput, { clearInput: true }); }, 200);
      }
    }
  };
  const handleMicTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    if (useBackendSTT) startBackendRecording(); else startListening();
  };
  const handleMicTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    const wasActive = pttActiveRef.current;
    if (useBackendSTT) {
      stopBackendRecording();
    } else {
      stopListening();
      if (wasActive) {
        setTimeout(() => { if (messageInput.trim()) sendMessage(messageInput, { clearInput: true }); }, 200);
      }
    }
  };

  useEffect(() => {
    if (!enableTTS || messages.length === 0) return;
    const lastAssistant = [...messages].filter(m => m.role === 'assistant').slice(-1)[0];
    if (!lastAssistant) return;
    if (lastTTSMessageIdRef.current === lastAssistant.id) return;
    const content = (lastAssistant.content || '').trim();
    if (!content) return;
    let isEnvelope = false;
    try {
      const parsed = JSON.parse(content);
      if (parsed && typeof parsed === 'object') {
        if ('type' in parsed) isEnvelope = true;
        if ('action' in parsed && 'success' in parsed) isEnvelope = true;
      }
    } catch {}
    if (isEnvelope) return;

    (async () => {
      try {
        lastTTSMessageIdRef.current = lastAssistant.id;
        try { ttsAudioRef.current?.pause(); } catch {}
        ttsAudioRef.current = null;
        const url = await requestTTS({ text: content });
        const audio = new Audio(url);
        ttsAudioRef.current = audio;
        await audio.play();
      } catch (e) {
        console.warn('TTS playback failed:', (e as any)?.message || e);
      }
    })();
  }, [messages, enableTTS]);

  if (!currentChat) {
    return (
      <div className="text-center py-12">
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-4xl font-bold text-white shadow-xl mx-auto mb-6">
          AI
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">AI Business Consultant</h2>
        <p className="text-lg text-gray-600 mb-2">Your intelligent business advisor</p>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium mb-8">
          <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
          Powered by Claude Sonnet 4.5
        </div>
        
        <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8 text-center">
          <div className="p-4 rounded-lg bg-white/50 border border-gray-200">
            <div className="text-2xl mb-2">📈</div>
            <div className="text-xs font-medium text-gray-700">Strategy</div>
          </div>
          <div className="p-4 rounded-lg bg-white/50 border border-gray-200">
            <div className="text-2xl mb-2">💼</div>
            <div className="text-xs font-medium text-gray-700">Marketing</div>
          </div>
          <div className="p-4 rounded-lg bg-white/50 border border-gray-200">
            <div className="text-2xl mb-2">💰</div>
            <div className="text-xs font-medium text-gray-700">Finance</div>
          </div>
        </div>
        
        <button
          onClick={handleCreateNewChat}
          className="inline-flex items-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-xl hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all font-semibold text-lg"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span>Start Conversation</span>
        </button>

        {chats.length > 0 && (
          <div className="mt-8 text-sm text-gray-600">
            Or select from {chats.length} previous conversation{chats.length !== 1 ? 's' : ''} in History
          </div>
        )}
      </div>
    );
  }

  const voiceSupported = useBackendSTT
    ? typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
    : speechSupported;

  return (
    <>
      {showToolsHelp && <ToolsHelpModal onClose={() => setShowToolsHelp(false)} />}
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="flex flex-col h-[600px] flex-1">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-white to-gray-50">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-xl font-bold text-white shadow-md">
            AI
          </div>
          <div>
            <h3 className="font-bold text-lg text-gray-900">AI Business Consultant</h3>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                <span className="text-xs font-medium text-green-700">Online</span>
              </div>
              {useBusinessContext && effectiveBusinessId && (
                <>
                  <span className="text-gray-300">•</span>
                  <span className="text-xs text-gray-600">
                    {businesses.find(b => b.id === effectiveBusinessId)?.name || 'Business Context'}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowToolsHelp(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition shadow-sm"
          title="View available tools and example phrases"
        >
          <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium text-gray-700">Tools Help</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
        {messages.length === 0 && currentChat && (
          <div className="space-y-4">
            <ProactiveInsights 
              organizationId={effectiveBusinessId}
              onInsightClick={(insight) => {
                setMessageInput(`Tell me more about: ${insight.title}`);
              }}
            />
            <ConversationStarters 
              consultantType={(currentChat as any).consultant_type || 'strategy'}
              onSelectPrompt={handleQuickPrompt}
            />
          </div>
        )}
        {messages.map((message) => (
          <div
            key={message.id}
            className={`group flex gap-3 ${
              message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
            }`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-100'
              }`}
            >
              {editingMessageId === message.id ? (
                <div className="space-y-2">
                  <textarea
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    className="w-full p-2 border rounded text-gray-900 min-h-[100px]"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveEdit}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                    >
                      Save & Regenerate
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="whitespace-pre-wrap break-words">
                  {(() => {
                    try {
                      const parsed = JSON.parse(message.content);
                      if (parsed.type === 'tool-cta') {
                        return (
                          <div className="flex flex-col gap-2">
                            <p>{parsed.text}</p>
                            <button
                              onClick={() => parsed.route && navigate(parsed.route)}
                              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                            >
                              {parsed.ctaLabel || 'Open Tool'}
                            </button>
                          </div>
                        );
                      }
                      if (parsed.type === 'advisor-insights') {
                        return renderAdvisorInsightsCard(parsed);
                      }
                      if (parsed.type === 'chat-memory') {
                        return renderChatMemoryCard(parsed);
                      }
                    } catch {
                      // Not JSON, render as text
                    }
                    return message.content;
                  })()}
                </div>
              )}
            </div>

            {/* Render rich tool result card for assistant messages with tool results */}
            {message.role === 'assistant' && !editingMessageId && isToolResult(message.content) && (
              <div className="mt-3 max-w-[80%]">
                <ToolResultCard
                  result={parseToolResult(message.content)!}
                  onAction={handleToolAction}
                />
              </div>
            )}
            {message.role === 'assistant' && (
              <MessageActions
                messageId={message.id}
                messageContent={message.content}
                onEdit={handleEditMessage}
                onRegenerate={handleRegenerateResponse}
                onFeedback={handleFeedback}
              />
            )}
            {message.role === 'user' && (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity mt-2">
                <button
                  onClick={() => handleEditMessage(message.id)}
                  className="text-xs text-gray-500 hover:text-blue-600 transition"
                >
                  ✏️ Edit
                </button>
              </div>
            )}
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start">
            <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-white shadow-sm ring-1 ring-gray-100">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="h-2 w-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="h-2 w-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="h-2 w-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                <span className="text-sm text-gray-500">AI is thinking...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-gray-200 bg-white p-4">
        <div className="flex gap-3 mb-3">
          <div className="flex-1">
            <textarea
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask anything about your business..."
              className="w-full rounded-xl border-gray-300 bg-gray-50 px-4 py-3 text-gray-900 placeholder-gray-500 shadow-sm transition focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 resize-none"
              rows={2}
            />
            {partialTranscript && (
              <div className="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
                🎤 {partialTranscript}
              </div>
            )}
            {speechError && (
              <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                {speechError}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onMouseDown={handleMicMouseDown}
              onMouseUp={handleMicMouseUp}
              onTouchStart={handleMicTouchStart}
              onTouchEnd={handleMicTouchEnd}
              disabled={!voiceSupported || isTyping}
              className={`rounded-xl px-4 py-3 font-medium shadow-sm transition disabled:opacity-50 ${
                isListening
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
              title={voiceSupported ? 'Hold to talk' : 'Voice not supported'}
            >
              {isListening ? '🔴 Recording' : '🎤 Voice'}
            </button>
            <button
              onClick={handleSendMessage}
              disabled={!messageInput.trim() || isTyping}
              className="rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTyping ? (
                <svg className="animate-spin h-5 w-5 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="h-5 w-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={useBusinessContext}
              onChange={(e) => {
                const next = e.target.checked;
                setUseBusinessContext(next);
                try {
                  localStorage.setItem('bc_use_business_context', String(next));
                } catch {
                }
              }}
            />
            <span>Use business context</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none" title="Voice messages send automatically">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={autoSendVoice}
              onChange={(e) => {
                const next = e.target.checked;
                setAutoSendVoice(next);
                try {
                  localStorage.setItem('bc_auto_send_voice', String(next));
                } catch {
                }
              }}
            />
            <span>Auto-send voice</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={enableTTS}
              onChange={(e) => {
                const next = e.target.checked;
                setEnableTTS(next);
                try {
                  localStorage.setItem('bc_enable_tts', String(next));
                } catch {
                }
              }}
            />
            <span>🔊 Voice responses</span>
          </label>
          <span className="text-gray-400">Press Enter to send, Shift+Enter for new line</span>
        </div>
      </div>
    </div>
        {shouldShowAdvisorPanel && (
          <div className="w-full lg:w-[320px] lg:shrink-0">
            <AdvisorInsightsPanel
              insights={currentAdvisorInsights}
              isLoading={advisorInsightsLoading}
              error={advisorInsightsError}
              onGenerate={handleGenerateAdvisorInsights}
              disabledReason={advisorPanelDisabledReason}
              isPremiumUser={isPremium}
              onAction={handleToolAction}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ChatTab;
